import React, { useContext, useState, useMemo, useEffect } from 'react';
import { GlobalContext } from '../context/GlobalContext';
import useApi from '../hooks/useApi';
import haxios from '../lib/haxios';
import { Loader2, Users, User, Plus, Search, X, Upload, FileText, MessageSquare, Building2, Tag } from 'lucide-react';
import CustomerProfileModal from '../components/CustomerProfileModal';

function Customers() {
  const { uinfo, apir } = useContext(GlobalContext);
  
  // Set document title
  useEffect(() => {
    document.title = 'Customer Manager - Sales Assist';
    return () => {
      document.title = 'Sales Assist';
    };
  }, []);
  
  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddProfile, setShowAddProfile] = useState(false);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showOnlyMine, setShowOnlyMine] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [filterBranch, setFilterBranch] = useState('All');
  const [filterHasRebates, setFilterHasRebates] = useState('All');
  const [filterHasMessages, setFilterHasMessages] = useState('All');

  // Import states
  const [showImportModal, setShowImportModal] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    console.log("Messages state changed (useEffect):", messages);
  }, [messages]);

  // Form state
  const [formData, setFormData] = useState({
    numberOfRamps: 0,
    numberOfTechnicians: 0,
    providesMOT: false,
    providesOilService: false,
    providesEVService: false,
    openingHours: {
      monday: { open: "", close: "" },
      tuesday: { open: "", close: "" },
      wednesday: { open: "", close: "" },
      thursday: { open: "", close: "" },
      friday: { open: "", close: "" },
      saturday: { open: "", close: "" },
      sunday: { open: "", close: "" }
    },
    initialComment: "",
    rebateStructure: {
      isGlobalPercent: true,
      tiers: []
    }
  });

  const profileSave = async (data) => {
    try {
      await haxios.put(`/visiondashboard/customer/profile/${selectedCustomer?.KeyCode}`, data);
    } catch (error) {
      throw new Error('Failed to save profile');
    }
  };

  const messageSave = async (data) => {
    try {
      await haxios.post(`/visiondashboard/customer/profile/${selectedCustomer?.KeyCode}/messages`, data);
    } catch (error) {
      throw new Error('Failed to save message');
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    try {
      const response = await haxios.post(
        `/visiondashboard/customer/profile/${selectedCustomer.KeyCode}/messages`,
        {
          staffMember: uinfo?.Name || "Unknown",
          message: newMessage.trim(),
          op: uinfo?.Inits || "Unknown"
        }
      );

      if (Array.isArray(response)) {
        setMessages(response);
        await fetchCustomers();
      } else {
        console.error("Unexpected response structure:", response);
        alert("Unexpected response from the server.");
      }
      setNewMessage("");
    } catch (error) {
      console.error("Error sending message:", error);
      alert("Failed to send message.");
    }
  };

  const handleImportFile = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setImporting(true);
    setImportResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await haxios.post('/visiondashboard/customer/rebate-tiers/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setImportResult({
        success: true,
        ...response.summary
      });

      // Refresh the customer list
      await fetchCustomers();
    } catch (error) {
      console.error('Import failed:', error);
      setImportResult({
        success: false,
        message: error.response?.data?.message || 'Import failed. Please try again.',
        errors: error.response?.data?.errors || []
      });
    } finally {
      setImporting(false);
      event.target.value = null; // Reset file input
    }
  };

  const ImportResultModal = ({ result, onClose }) => {
    if (!result) return null;

    return (
      <div style={{ maxHeight: '100%' }} className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div style={{ maxHeight: 'inherit' }} className="bg-white rounded-lg p-6 max-w-md w-full shadow-xl">
          <div className="flex items-center gap-2 mb-4">
            {result.success ? (
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-600 text-sm">✓</span>
              </div>
            ) : (
              <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                <span className="text-red-600 text-sm">✗</span>
              </div>
            )}
            <h3 className="text-lg font-semibold">
              {result.success ? 'Import Successful' : 'Import Failed'}
            </h3>
          </div>

          {result.success ? (
            <div style={{ maxHeight: '70vh', overflow: 'scroll' }} className="space-y-2 text-sm text-gray-600">
              <p>• Profiles processed: {result.profilesProcessed}</p>
              <p>• Tiers imported: {result.tiersImported}</p>
              {result.errors && result.errors.length > 0 && (
                <div className="mt-4">
                  <p className="text-red-600 font-medium">Warnings:</p>
                  <ul className="list-disc list-inside text-red-600">
                    {result.errors.map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-2 text-sm text-red-600">
              <p>{result.message}</p>
              {result.errors && result.errors.map((error, index) => (
                <p key={index}>{error}</p>
              ))}
            </div>
          )}

          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  const handleSaveProfile = async () => {
    try {
      // Validate rebate tiers are in ascending order
      const tiers = formData.rebateStructure.tiers;
      for (let i = 1; i < tiers.length; i++) {
        if (tiers[i].target <= tiers[i - 1].target) {
          alert('Rebate tiers must be in ascending order');
          return;
        }
      }

      const profileData = {
        keyCode: selectedCustomer.KeyCode,
        numberOfRamps: formData.numberOfRamps,
        numberOfTechnicians: formData.numberOfTechnicians,
        providesMOT: formData.providesMOT,
        providesOilService: formData.providesOilService,
        providesEVService: formData.providesEVService,
        openingHours: formData.openingHours,
        rebateStructure: formData.rebateStructure
      };

      if (selectedCustomer.profile) {
        await profileSave(profileData);
      } else {
        await profileSave(profileData);
      }

      if (formData.initialComment) {
        await messageSave({
          staffMember: uinfo?.Inits || 'Unknown',
          message: formData.initialComment
        });
      }

      await fetchCustomers();
      setShowProfileDialog(false);
      setShowAddProfile(false);
      setSelectedCustomer(null);
      setFormData({
        numberOfRamps: 0,
        numberOfTechnicians: 0,
        providesMOT: false,
        providesOilService: false,
        providesEVService: false,
        openingHours: {
          monday: { open: "", close: "" },
          tuesday: { open: "", close: "" },
          wednesday: { open: "", close: "" },
          thursday: { open: "", close: "" },
          friday: { open: "", close: "" },
          saturday: { open: "", close: "" },
          sunday: { open: "", close: "" }
        },
        initialComment: "",
        rebateStructure: {
          isGlobalPercent: true,
          tiers: []
        }
      });
    } catch (error) {
      console.error('Failed to save:', error);
      alert('Failed to save profile. Please try again.');
    }
  };

  // Fetch customers that have profiles
  const {
    data: customersWithProfiles,
    isLoading: loadingProfiles,
    refetch: fetchCustomers
  } = useApi({
    url: `/visiondashboard/customers/profiles${showOnlyMine ? `?rep=${uinfo?.Inits}` : ''}`,
    initial: [],
    enabled: !!apir
  });

  useEffect(() => {
    console.log("Customer profiles data:", customersWithProfiles);
  }, [customersWithProfiles]);

  // Fetch customers without profiles (for dropdown)
  const {
    data: customersWithoutProfiles,
    isLoading: loadingDropdown
  } = useApi({
    url: `/visiondashboard/customers/without-profiles${showOnlyMine ? `?rep=${uinfo?.Inits}` : ''}`,
    initial: [],
    enabled: showAddProfile && !!apir
  });

  // Filter dropdown customers
  const filteredDropdownCustomers = useMemo(() => {
    if (!customerSearchTerm) return customersWithoutProfiles;

    const search = customerSearchTerm.toLowerCase();
    return customersWithoutProfiles.filter(customer =>
      customer.KeyCode.toLowerCase().includes(search) ||
      customer.Name.toLowerCase().includes(search)
    );
  }, [customersWithoutProfiles, customerSearchTerm]);

  // Filter displayed profiles
  const filteredCustomers = useMemo(() => {
    if (!customersWithProfiles) return [];
    
    let filtered = customersWithProfiles.filter(customer => {
      if (!customer) return false;
      
      // Search filter
      if (searchTerm) {
        const search = searchTerm.toLowerCase();
        const matchesSearch = 
          customer.KeyCode?.toLowerCase().includes(search) ||
          customer.Name?.toLowerCase().includes(search) ||
          customer.Addra?.toLowerCase().includes(search) ||
          customer.Branch?.toLowerCase().includes(search);
        if (!matchesSearch) return false;
      }
      
      // Branch filter
      if (filterBranch !== 'All' && customer.Branch !== filterBranch) {
        return false;
      }
      
      // Rebates filter
      if (filterHasRebates === 'Yes' && (!customer.profile?.rebateStructure?.tiers || customer.profile.rebateStructure.tiers.length === 0)) {
        return false;
      }
      if (filterHasRebates === 'No' && customer.profile?.rebateStructure?.tiers && customer.profile.rebateStructure.tiers.length > 0) {
        return false;
      }
      
      // Messages filter
      if (filterHasMessages === 'Yes' && (!customer.profile?.messages || customer.profile.messages.length === 0)) {
        return false;
      }
      if (filterHasMessages === 'No' && customer.profile?.messages && customer.profile.messages.length > 0) {
        return false;
      }
      
      return true;
    });
    
    return filtered;
  }, [customersWithProfiles, searchTerm, filterBranch, filterHasRebates, filterHasMessages]);

  // Calculate KPI stats
  const kpiStats = useMemo(() => {
    if (!customersWithProfiles) return { total: 0, withMessages: 0, withRebates: 0, withServices: 0 };
    
    return {
      total: customersWithProfiles.length,
      withMessages: customersWithProfiles.filter(c => c.profile?.messages && c.profile.messages.length > 0).length,
      withRebates: customersWithProfiles.filter(c => c.profile?.rebateStructure?.tiers && c.profile.rebateStructure.tiers.length > 0).length,
      withServices: customersWithProfiles.filter(c => 
        c.profile?.providesMOT || c.profile?.providesOilService || c.profile?.providesEVService
      ).length
    };
  }, [customersWithProfiles]);

  // Get unique branches for filter
  const uniqueBranches = useMemo(() => {
    if (!customersWithProfiles) return [];
    const branches = [...new Set(customersWithProfiles.map(c => c.Branch).filter(Boolean))];
    return branches.sort();
  }, [customersWithProfiles]);

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 rounded-lg bg-purple-100">
              <Users className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Customer Manager</h1>
              <p className="text-sm text-gray-600 mt-1">Manage customer profiles and relationships</p>
            </div>
          </div>
          <div className="flex gap-3">
            {/* Import Button */}
            <div className="relative">
              <input
                type="file"
                id="rebate-import"
                accept=".csv,.xls,.xlsx"
                onChange={handleImportFile}
                className="hidden"
              />
              <label
                htmlFor="rebate-import"
                className={`flex items-center gap-2 px-4 py-2 rounded-lg cursor-pointer transition-colors ${importing
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 shadow-sm'
                  }`}
              >
                {importing ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Upload className="h-5 w-5" />
                )}
                {importing ? 'Importing...' : 'Import Rebates'}
              </label>
            </div>

            {/* Add Profile Button */}
            {showAddProfile ? (
              <div className="flex items-center gap-2">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <input
                      type="text"
                      placeholder="Search customers..."
                      value={customerSearchTerm}
                      onChange={(e) => setCustomerSearchTerm(e.target.value)}
                      className="pl-9 p-2 border border-gray-300 rounded-lg w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <select
                    value={selectedCustomer?.KeyCode || ''}
                    onChange={(e) => {
                      const customer = customersWithoutProfiles.find(c => c.KeyCode === e.target.value);
                      setSelectedCustomer(customer);
                    }}
                    className="mt-2 p-2 border border-gray-300 rounded-lg w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    size={5}
                  >
                    <option value="">Select a customer...</option>
                    {filteredDropdownCustomers.map((customer) => (
                      <option key={customer.KeyCode} value={customer.KeyCode}>
                        {customer.Name} ({customer.KeyCode})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => {
                      if (selectedCustomer) {
                        setFormData({
                          numberOfRamps: 0,
                          numberOfTechnicians: 0,
                          providesMOT: false,
                          providesOilService: false,
                          providesEVService: false,
                          openingHours: {
                            monday: { open: "", close: "" },
                            tuesday: { open: "", close: "" },
                            wednesday: { open: "", close: "" },
                            thursday: { open: "", close: "" },
                            friday: { open: "", close: "" },
                            saturday: { open: "", close: "" },
                            sunday: { open: "", close: "" }
                          },
                          initialComment: "",
                          rebateStructure: {
                            isGlobalPercent: true,
                            tiers: []
                          }
                        });
                        setShowProfileDialog(true);
                      }
                    }}
                    disabled={!selectedCustomer}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                  >
                    Create Profile
                  </button>
                  <button
                    onClick={() => {
                      setShowAddProfile(false);
                      setSelectedCustomer(null);
                      setCustomerSearchTerm('');
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => {
                  setShowAddProfile(true);
                  setFormData({
                    numberOfRamps: 0,
                    numberOfTechnicians: 0,
                    providesMOT: false,
                    providesOilService: false,
                    providesEVService: false,
                    openingHours: {
                      monday: { open: "", close: "" },
                      tuesday: { open: "", close: "" },
                      wednesday: { open: "", close: "" },
                      thursday: { open: "", close: "" },
                      friday: { open: "", close: "" },
                      saturday: { open: "", close: "" },
                      sunday: { open: "", close: "" }
                    },
                    initialComment: "",
                    rebateStructure: {
                      isGlobalPercent: true,
                      tiers: []
                    }
                  });
                }}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-sm"
              >
                <Plus className="h-5 w-5" />
                Add Profile
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="py-6">
        <div className="px-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-purple-500">
              <div className="text-sm font-medium text-gray-600 mb-1">Total Customers</div>
              <div className="text-2xl font-bold text-gray-900">{kpiStats.total}</div>
              <div className="text-xs text-gray-500 mt-1">With profiles</div>
            </div>
            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-blue-500">
              <div className="text-sm font-medium text-gray-600 mb-1">With Messages</div>
              <div className="text-2xl font-bold text-gray-900">{kpiStats.withMessages}</div>
              <div className="text-xs text-gray-500 mt-1">{kpiStats.total > 0 ? Math.round((kpiStats.withMessages / kpiStats.total) * 100) : 0}% of total</div>
            </div>
            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-orange-500">
              <div className="text-sm font-medium text-gray-600 mb-1">With Rebates</div>
              <div className="text-2xl font-bold text-gray-900">{kpiStats.withRebates}</div>
              <div className="text-xs text-gray-500 mt-1">{kpiStats.total > 0 ? Math.round((kpiStats.withRebates / kpiStats.total) * 100) : 0}% of total</div>
            </div>
            <div className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm border-l-4 border-l-green-500">
              <div className="text-sm font-medium text-gray-600 mb-1">With Services</div>
              <div className="text-2xl font-bold text-gray-900">{kpiStats.withServices}</div>
              <div className="text-xs text-gray-500 mt-1">{kpiStats.total > 0 ? Math.round((kpiStats.withServices / kpiStats.total) * 100) : 0}% of total</div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                  <input
                    type="text"
                    placeholder="Search profiles..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Branch</label>
                <select
                  value={filterBranch}
                  onChange={(e) => setFilterBranch(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="All">All Branches</option>
                  {uniqueBranches.map(branch => (
                    <option key={branch} value={branch}>{branch}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Has Rebates</label>
                <select
                  value={filterHasRebates}
                  onChange={(e) => setFilterHasRebates(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="All">All</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Has Messages</label>
                <select
                  value={filterHasMessages}
                  onChange={(e) => setFilterHasMessages(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="All">All</option>
                  <option value="Yes">Yes</option>
                  <option value="No">No</option>
                </select>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-4">
              <button
                onClick={() => setShowOnlyMine(!showOnlyMine)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${showOnlyMine
                  ? 'bg-blue-50 border-blue-200 text-blue-700'
                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}
              >
                {showOnlyMine ? (
                  <User className="h-5 w-5" />
                ) : (
                  <Users className="h-5 w-5" />
                )}
                {showOnlyMine ? 'My Customers' : 'All Customers'}
              </button>
              {(searchTerm || filterBranch !== 'All' || filterHasRebates !== 'All' || filterHasMessages !== 'All') && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setFilterBranch('All');
                    setFilterHasRebates('All');
                    setFilterHasMessages('All');
                  }}
                  className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <X className="h-4 w-4" />
                  Clear Filters
                </button>
              )}
            </div>
          </div>

          {/* Profile List Table */}
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Account</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Branch</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Messages</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ramps</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Techs</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Services</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rebates</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loadingProfiles ? (
                    <tr>
                      <td colSpan="9" className="px-4 py-8 text-center">
                        <div className="flex items-center justify-center">
                          <Loader2 className="h-6 w-6 animate-spin text-gray-400 mr-2" />
                          <span className="text-gray-500">Loading customers...</span>
                        </div>
                      </td>
                    </tr>
                  ) : filteredCustomers.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="px-4 py-8 text-center text-gray-500">
                        No profiles found. {searchTerm || filterBranch !== 'All' || filterHasRebates !== 'All' || filterHasMessages !== 'All' ? 'Try adjusting your filters.' : 'Add a profile to get started.'}
                      </td>
                    </tr>
                  ) : (
                    filteredCustomers.map((customer) => (
                      <tr key={customer.KeyCode} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm font-semibold text-gray-900">{customer.KeyCode}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{customer.Name}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            {customer.Branch || 'N/A'}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-1">
                            <MessageSquare className="h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-900">{customer.profile?.messages?.length || 0}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-sm text-gray-900">{customer.profile?.numberOfRamps || 0}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className="text-sm text-gray-900">{customer.profile?.numberOfTechnicians || 0}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex gap-1.5 flex-wrap">
                            {customer.profile?.providesMOT && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                MOT
                              </span>
                            )}
                            {customer.profile?.providesOilService && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                Oil
                              </span>
                            )}
                            {customer.profile?.providesEVService && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                EV
                              </span>
                            )}
                            {!customer.profile?.providesMOT && !customer.profile?.providesOilService && !customer.profile?.providesEVService && (
                              <span className="text-xs text-gray-400">None</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {customer.profile?.rebateStructure?.tiers?.length > 0 ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                              {customer.profile.rebateStructure.isGlobalPercent ? 'Global' : 'Bracket'} - {customer.profile.rebateStructure.tiers.length} tier{customer.profile.rebateStructure.tiers.length !== 1 ? 's' : ''}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">None</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <button
                            onClick={async () => {
                              setSelectedCustomer(customer);
                              setFormData({
                                numberOfRamps: customer.profile?.numberOfRamps || 0,
                                numberOfTechnicians: customer.profile?.numberOfTechnicians || 0,
                                providesMOT: customer.profile?.providesMOT || false,
                                providesOilService: customer.profile?.providesOilService || false,
                                providesEVService: customer.profile?.providesEVService || false,
                                openingHours: customer.profile?.openingHours || {
                                  monday: { open: "", close: "" },
                                  tuesday: { open: "", close: "" },
                                  wednesday: { open: "", close: "" },
                                  thursday: { open: "", close: "" },
                                  friday: { open: "", close: "" },
                                  saturday: { open: "", close: "" },
                                  sunday: { open: "", close: "" },
                                },
                                initialComment: "",
                                rebateStructure: customer.profile?.rebateStructure || {
                                  isGlobalPercent: true,
                                  tiers: []
                                }
                              });

                              try {
                                const response = await haxios.get(`/salesassistnew/customer/profile/${customer.KeyCode}/messages`);
                                if (Array.isArray(response) && response.length > 0) {
                                  setMessages(response);
                                } else {
                                  setMessages([]);
                                }
                              } catch (error) {
                                console.error("Error fetching messages:", error);
                                setMessages([]);
                              }

                              setShowProfileDialog(true);
                            }}
                            className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 px-3 py-1.5 rounded-lg transition-colors text-sm font-medium"
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Profile Creation Modal */}
      {showProfileDialog && (
        <CustomerProfileModal
          selectedCustomer={selectedCustomer}
          formData={formData}
          setFormData={setFormData}
          messages={messages}
          newMessage={newMessage}
          setNewMessage={setNewMessage}
          handleSendMessage={handleSendMessage}
          handleSaveProfile={handleSaveProfile}
          onClose={() => setShowProfileDialog(false)}
          uinfo={uinfo}
        />
      )}

      {/* Import Result Modal */}
      <ImportResultModal
        result={importResult}
        onClose={() => {
          setImportResult(null);
          setShowImportModal(false);
        }}
      />
    </div>
  );
}

export default Customers;
